import React from 'react';
import { Control, useController } from 'react-hook-form';
import { Choice } from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';
import { cn } from '@/utils/styles';
import { Card, CardContent } from '@design-system/Card';
import { TickMarkIcon } from '@/components/icons/TickMarkIcon';

interface Props {
  control: Control<Record<string, unknown>>;
  name: string;
  title: string;
  subtitle?: string;
  options: Choice[];
  required?: boolean;
  maxSelections?: number;
  disabled?: boolean;
  canSortSelection?: boolean;
}

const MultiSelectWrapper: React.FC<Props> = ({
  control,
  name,
  options,
  required = false,
  maxSelections,
  disabled = false,
  canSortSelection = false,
}) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
    rules: {
      required: required ? 'Please select at least one option' : false,
      validate: {
        maxSelections: (value: unknown) => {
          if (!Array.isArray(value)) return 'Invalid selection';
          if (maxSelections && value.length > maxSelections) {
            return `Please select up to ${maxSelections} option${
              maxSelections > 1 ? 's' : ''
            }`;
          }
          return true;
        },
      },
    },
  });

  const selectedItems = (
    Array.isArray(field.value) ? field.value : []
  ) as string[];

  const handleSelect = (option: Choice) => {
    if (disabled) return;

    const isExclusiveOption =
      option.label.toLowerCase().includes('none') ||
      option.label.toLowerCase().includes("don't know") ||
      option.label.toLowerCase().includes('don’t know') ||
      option.label.toLowerCase().includes("i don't know") ||
      option.label.toLowerCase().includes('i don’t know');

    let newSelectedItems: string[];

    if (selectedItems.includes(option.label)) {
      // Remove the option if it's already selected
      newSelectedItems = selectedItems.filter((item) => item !== option.label);
    } else {
      // Check if there's already an exclusive option selected
      const hasExclusiveSelected = selectedItems.some((item) => {
        const lowerItem = item.toLowerCase();
        return (
          lowerItem.includes('none') ||
          lowerItem.includes("don't know") ||
          lowerItem.includes("don’t know") ||
          lowerItem.includes("i don't know") ||
          lowerItem.includes("i don’t know")
        );
      });

      if (isExclusiveOption) {
        // If selecting exclusive option, clear all other selections
        newSelectedItems = [option.label];
      } else if (hasExclusiveSelected) {
        // If an exclusive option is already selected, prevent adding new options
        return;
      } else {
        // If selecting any other option, remove exclusive options if they exist
        const filteredItems = selectedItems.filter((item) => {
          const lowerItem = item.toLowerCase();
          return !(
            lowerItem.includes('none') ||
            lowerItem.includes("don't know") ||
            lowerItem.includes("don’t know") ||
            lowerItem.includes("i don't know") ||
            lowerItem.includes("i don’t know")
          );
        });
        newSelectedItems = [...filteredItems, option.label];
      }
    }

    // Check max selections
    if (maxSelections && newSelectedItems.length > maxSelections) {
      return;
    }

    field.onChange(newSelectedItems);
  };

  return (
    <div className="w-full">
      <Card className="border-transparent bg-transparent">
        <CardContent className="p-1">
          <div className="space-y-3">
            {options?.map((option) => {
              const isSelected = selectedItems.includes(option.label);
              const selectionNumber = isSelected
                ? selectedItems.indexOf(option.label) + 1
                : null;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !disabled && handleSelect(option)}
                  className={cn(
                    'w-full flex items-center justify-between px-6 py-4',
                    'bg-white rounded-full border-2 transition-colors duration-200',
                    isSelected ? 'border-[#146464]' : 'border-gray-200',
                    disabled && 'opacity-50 cursor-not-allowed',
                  )}
                  disabled={disabled}
                >
                  <span className="typography-btn-large text-left">
                    {option.label}
                  </span>
                  <div
                    className={cn(
                      'h-6 w-6 rounded border-2 flex items-center justify-center',
                      isSelected ? 'bg-black border-black' : 'border-gray-300',
                    )}
                  >
                    {isSelected && canSortSelection ? (
                      <span className="text-white text-sm font-medium">
                        {selectionNumber}
                      </span>
                    ) : isSelected ? (
                      <TickMarkIcon
                        className="h-4 w-4"
                        whiteTick={true}
                        strokeWidth={1}
                      />
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
          {error && <p className="text-red-500 mt-2">{error.message}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiSelectWrapper;
